// src/pages/RestaurantSite/components/Carrousel/Carrousel.tsx
import React, { useState, useEffect, useRef } from 'react'
import { ChevronLeft, ChevronRight } from 'react-bootstrap-icons'
import { LocalSiteConfig, Promotion } from '../../types'
import './Carrousel.scss'
import { Button } from 'react-bootstrap'

interface CarrouselProps {
  config: LocalSiteConfig
}

const Carrousel: React.FC<CarrouselProps> = ({ config }) => {
  const promos: Promotion[] = config.promotions || []
  const [current, setCurrent] = useState(0)

  // touch swipe refs
  const touchStartX = useRef<number | null>(null)
  const touchDeltaX = useRef<number>(0)
  const swipeThreshold = 40 // px

  // auto-rotate every 6s
  useEffect(() => {
    if (promos.length < 2) return
    const iv = setInterval(
      () => setCurrent(i => (i + 1) % promos.length),
      6000
    )
    return () => clearInterval(iv)
  }, [promos])

  if (!config.show_promotions || promos.length === 0) return null

  const active = promos[current]

  const handleTouchStart = (e: React.TouchEvent) => {
    if (promos.length < 2) return
    touchStartX.current = e.touches[0].clientX
    touchDeltaX.current = 0
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return
    const x = e.touches[0].clientX
    touchDeltaX.current = x - touchStartX.current
  }

  const handleTouchEnd = () => {
    if (touchStartX.current === null) return
    const delta = touchDeltaX.current
    touchStartX.current = null
    touchDeltaX.current = 0
    if (Math.abs(delta) < swipeThreshold) return
    if (delta > 0) {
      // swipe right -> previous
      setCurrent(c => (c - 1 + promos.length) % promos.length)
    } else {
      // swipe left -> next
      setCurrent(c => (c + 1) % promos.length)
    }
  }

  return (
    <div
      className={`carousel-container ${active.promotion_image ? '' : 'no-image'}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* SLIDES */}
      {promos.map((p, i) => (
        <div
          key={p.promotion_id}
          className={`slide${i === current ? ' active' : ''}${!p.promotion_image ? ' no-image' : ''}`}
          style={p.promotion_image ? { backgroundImage: `url(${p.promotion_image})` } : {}}
        />
      ))}

      {/* OVERLAY CONTENT */}
      <div className="slide-content">
        <h3>{active.promotion_name}</h3>
        <div className="scanlines" />
        <p>{active.promotion_description}</p>
        {active.promotion_url && (
          <a href={active.promotion_url} target="_blank" rel="noopener noreferrer">
            Más información →
          </a>
        )}
      </div>

      {/* NAVIGATION */}
      {promos.length > 1 && (
        <>
          <Button
            className="nav prev"
            variant='none'
            onClick={() => setCurrent((current - 1 + promos.length) % promos.length)}
            aria-label="Anterior promo"
          >
            <ChevronLeft />
          </Button>
          <Button
            className="nav next"
            variant='none'
            onClick={() => setCurrent((current + 1) % promos.length)}
            aria-label="Siguiente promo"
          >
            <ChevronRight />
          </Button>
        </>
      )}
    </div>
  )
}

export default Carrousel
